<template>
<section v-if="items.length" class="articles-slider">
  <h2 v-if="title" class="title">{{ title }}</h2>

  <ClientOnly>
    <div class="slider">
      <swiper-container ref="swiperRef" class="swiper">
        <swiper-slide v-for="(item, idx) in items" :key="idx" class="swiper-slide">
          <slot :item="item" :index="idx">
            <ArticleCard :card="item" />
          </slot>
        </swiper-slide>
      </swiper-container>
    </div>

    <template v-if="showArrows">
      <BaseButton
        class="button-control left"
        icon-left="arrow/arrow-left-2"
        size="xl"
        @click="swiper.prev()"
      />
      <BaseButton
        class="button-control right"
        icon-left="arrow/arrow-right-3"
        size="xl"
        @click="swiper.next()"
      />
    </template>
  </ClientOnly>

  <BaseButtonLink
    v-if="buttonTitle && buttonLink"
    class="view-all"
    :name="buttonTitle"
    :link="buttonLink"
  />
</section>
</template>

<script lang="ts" setup>
import ArticleCard from './ArticleCard.vue'
import { BaseButton, BaseButtonLink } from '~/components/base-ui/buttons'
import type { ArticleMainItem } from '~/models/articles'

const SLIDES_PER_VIEW_DESKTOP = 3

const props = withDefaults(
  defineProps<{
    items: ArticleMainItem[]
    title?: string
    buttonTitle?: string
    buttonLink?: string
  }>(),
  {
    title: '',
    buttonTitle: '',
    buttonLink: '',
  }
)

defineSlots<{
  default?: (props: { item: ArticleMainItem, index: number }) => unknown
}>()

const swiperRef = ref(null)

const showArrows = computed(() => props.items.length > SLIDES_PER_VIEW_DESKTOP)

const swiper = useSwiper(swiperRef, {
  slidesPerView: SLIDES_PER_VIEW_DESKTOP,
  spaceBetween: 24,
  breakpoints: {
    0: { slidesPerView: 'auto', spaceBetween: 16 },
    1280: { slidesPerView: SLIDES_PER_VIEW_DESKTOP, spaceBetween: 20 },
    1440: { slidesPerView: SLIDES_PER_VIEW_DESKTOP, spaceBetween: 24 },
  },
})
</script>

<style lang="scss" scoped>
.articles-slider {
  position: relative;
  @extend %flex-column;

  gap: 32px;

  @include vp-1439 {
    gap: 24px;
  }

  @include vp-767 {
    gap: 16px;
  }

  >.title {
    font-size: 32px;
    font-weight: 500;
    line-height: 40px;
    color: $color-potato-90;

    @include vp-767 {
      font-size: 24px;
      line-height: 32px;
    }
  }

  >.view-all {
    margin: 0 auto;

    @include vp-767 {
      width: 100%;
    }
  }

  >.slider {
    position: relative;
    overflow: hidden;

    >.swiper {
      display: block;

      .swiper-slide {
        height: auto;

        @include vp-1279 {
          width: 320px;
        }
      }
    }
  }

  >.button-control {
    z-index: 5;
    position: absolute;
    top: 192px;
    width: 48px;
    height: 48px;
    color: $color-default-black;
    background-color: $color-default-white;
    box-shadow: 0 4px 20px 0 rgb(0 0 0 / 8%);

    &.left {
      left: -20px;
    }

    &.right {
      right: -20px;
    }

    @include vp-1279 {
      display: none;
    }
  }
}
</style>
